import { Placement, RtlSide } from '../UNSAFE_Floating';
import {
  useAnimationStatus,
  AnimationStatus,
  AnimationStatusProps
} from '../hooks/PRIVATE_useAnimationStatus';
import { normalizePosition } from '../utils/PRIVATE_floatingUtils';
import { useUser } from '../hooks/UNSAFE_useUser';
import { useEffect } from 'preact/hooks';

/**
 * The placement and direction aware HTML element
 * The useAnimation hook do not allow to change dynamically the animation states,
 * but allows a function that returns animation states config. The function takes
 * the animated node of type <E extends HTMLElement> as an argument. This could be used
 * to extend HTMLElement with placement and direction.
 */
type AnimationPopupElement = HTMLElement & {
  placement?: Placement;
  direction?: 'ltr' | 'rtl';
};

type PopupAnimationProps = Omit<AnimationStatusProps, 'animationStates'> & {
  placement: Placement;
};

const animationStates = {
  opening: (node: AnimationPopupElement) => {
    const realPlacement = logicalSide(node.placement, node.direction);
    return {
      //zoomIn
      from: {
        scaleX: 0,
        scaleY: 0,
        opacity: 0,
        transformOrigin: _ANIMATION_TRANSFORM_ORIGIN_RULES[realPlacement]
      },
      to: {
        scaleX: 1,
        scaleY: 1,
        opacity: 1,
        transformOrigin: _ANIMATION_TRANSFORM_ORIGIN_RULES[realPlacement]
      },
      options: {
        duration: 200
      }
    };
  },
  closing: (node: AnimationPopupElement) => {
    const realPlacement = logicalSide(node.placement, node.direction);
    return {
      //zoomOut
      from: {
        scaleX: 1,
        scaleY: 1,
        opacity: 1,
        transformOrigin: _ANIMATION_TRANSFORM_ORIGIN_RULES[realPlacement]
      },
      to: {
        scaleX: 0,
        scaleY: 0,
        opacity: 0,
        transformOrigin: _ANIMATION_TRANSFORM_ORIGIN_RULES[realPlacement]
      },
      options: {
        duration: 200
      }
    };
  }
};

const _ANIMATION_TRANSFORM_ORIGIN_RULES = {
  top: 'center bottom',
  'top-right': 'right bottom',
  'top-left': 'left bottom',
  'top-right-corner': 'left bottom',
  'top-left-corner': 'right bottom',
  right: 'left center',
  'right-top': 'left top',
  'right-bottom': 'left bottom',
  'right-top-corner': 'left bottom',
  'right-bottom-corner': 'left top',
  bottom: 'center top',
  'bottom-right': 'right top',
  'bottom-left': 'left top',
  'bottom-right-corner': 'left top',
  'bottom-left-corner': 'right top',
  left: 'right center',
  'left-top': 'right top',
  'left-bottom': 'right bottom',
  'left-top-corner': 'right bottom',
  'left-bottom-corner': 'right top'
};

type RealPlacement =
  | 'top'
  | 'top-right'
  | 'top-left'
  | 'top-right-corner'
  | 'top-left-corner'
  | 'right'
  | 'right-top'
  | 'right-bottom'
  | 'right-top-corner'
  | 'right-bottom-corner'
  | 'bottom'
  | 'bottom-right'
  | 'bottom-left'
  | 'bottom-right-corner'
  | 'bottom-left-corner'
  | 'left'
  | 'left-top'
  | 'left-bottom'
  | 'left-top-corner'
  | 'left-bottom-corner';

const logicalSide = (
  placement: Placement | undefined,
  direction: 'rtl' | 'ltr' | undefined
): RealPlacement => {
  if (placement == null || direction == null) return 'bottom';
  const side = placement.split('-')[0] as RtlSide;
  const alignment = placement.split('-')[1] as RtlSide;
  const corner = placement.split('-')[2];
  const physicalSide = normalizePosition(side, direction);
  const placements: Array<RealPlacement> = [
    'top',
    'top-right',
    'top-left',
    'top-right-corner',
    'top-left-corner',
    'right',
    'right-top',
    'right-bottom',
    'right-top-corner',
    'right-bottom-corner',
    'bottom',
    'bottom-right',
    'bottom-left',
    'bottom-right-corner',
    'bottom-left-corner',
    'left',
    'left-top',
    'left-bottom',
    'left-top-corner',
    'left-bottom-corner'
  ];
  const newPlacement: RealPlacement = placements.filter(
    (placement) =>
      (alignment &&
        corner &&
        placement === `${physicalSide}-${normalizePosition(alignment, direction)}-${corner}`) ||
      (alignment &&
        !corner &&
        placement === `${physicalSide}-${normalizePosition(alignment, direction)}`) ||
      (!alignment && placement === `${physicalSide}`)
  )[0];
  if (!newPlacement) {
    return 'bottom';
  }

  return newPlacement;
};

export function usePopupAnimation(props: PopupAnimationProps): {
  status: AnimationStatus;
  setAnimationElementRef: (node: HTMLElement | null) => void;
} {
  const { isOpen, isAnimatedOnMount, onTransitionEnd, placement } = props;
  const { direction } = useUser();
  const { setAnimationElementRef, animationElementRef, status } =
    useAnimationStatus<AnimationPopupElement>({
      isOpen: isOpen,
      isAnimatedOnMount: isAnimatedOnMount,
      animationStates,
      onTransitionEnd
    });
  const popupElementRef = animationElementRef;

  //This is needed for the case if popup flips and changes the placement.
  useEffect(() => {
    if (popupElementRef != null && popupElementRef.current != null && status != 'unmounted') {
      popupElementRef.current.placement = placement;
      popupElementRef.current.direction = direction;
    }
  }, [popupElementRef, placement, direction, status]);
  return { setAnimationElementRef, status };
}
